import { Resource } from "@medplum/fhirtypes";
import {
  getEvidenceResourceDisplay,
  getEvidenceResourceDate,
} from "../../shared-components/fhir-evidence-resource-display";
import {
  compareEvidenceResourcesDesc,
  evidenceResourceListKey,
} from "../../../../utils/evidence-resource-list-utils";
import { Box, Text, VStack, HStack, Badge, Flex, Separator, Heading } from "@chakra-ui/react-v3";
import { SuspectRowData } from "../tables/suspect";
import { SidePanelLayout } from "../components/side-panel-sections/side-panel-layout";
import { ResourceAnalysis } from "../components/resource-analysis";

export type SuspectSidePanelContentProps = {
  data: SuspectRowData;
  /** V1 patient overview has no analysis pipeline; omit AI block when false. */
  isAiAnalysisEnabled?: boolean;
};

/**
 * Side panel content for suspect conditions.
 * Shows condition details, detection logic, and supporting FHIR evidence resources.
 * Close button is handled generically by SidePanelDrawer.
 */
export function SuspectSidePanelContent({
  data,
  isAiAnalysisEnabled = true,
}: SuspectSidePanelContentProps) {
  return (
    <>
      <SidePanelLayout title="Suspect Condition" subtitle={data.conditionName}>
        {isAiAnalysisEnabled ? <ResourceAnalysis /> : null}

        {data.detectionLogic && (
          <Box>
            <VStack align="start" gap={1}>
              <Text fontSize="sm" fontWeight="medium" color="fg">
                Detection Logic:
              </Text>
              <Text fontSize="sm" color="fg.muted">
                {data.detectionLogic}
              </Text>
            </VStack>
          </Box>
        )}

        <Separator borderColor="border" />

        <EvidenceResourcesSection evidenceResources={data.evidenceResources} />
      </SidePanelLayout>
    </>
  );
}

function EvidenceResourcesSection({ evidenceResources }: { evidenceResources: Resource[] }) {
  if (evidenceResources.length === 0) {
    return (
      <Box>
        <Heading size="md" mb={3} color="fg">
          Evidence
        </Heading>
        <Text fontSize="sm" color="fg.muted">
          No evidence found
        </Text>
      </Box>
    );
  }

  const sorted = [...evidenceResources].sort(function sortEvidenceDesc(a, b) {
    return compareEvidenceResourcesDesc(a, b, getEvidenceResourceDate);
  });

  return (
    <Box>
      <Heading size="md" mb={3} color="fg">
        Evidence ({evidenceResources.length})
      </Heading>
      <VStack gap={2} align="stretch">
        {sorted.map((resource, index) => (
          <EvidenceResourceCard
            key={evidenceResourceListKey(resource, index)}
            resource={resource}
          />
        ))}
      </VStack>
    </Box>
  );
}

function EvidenceResourceCard({ resource }: { resource: Resource }) {
  const display = getEvidenceResourceDisplay(resource);

  return (
    <Box border="1px" borderColor="border" borderRadius="lg" p={4} bg="bg" shadow="sm">
      <VStack gap={3} align="stretch">
        <Flex justify="space-between" align="start" gap={3}>
          <HStack gap={3} flexWrap="wrap">
            <Badge
              colorPalette={display.colorPalette}
              variant="subtle"
              fontSize="xs"
              px={2}
              py={1}
              flexShrink={0}
            >
              {resource.resourceType}
            </Badge>
            <Text fontSize="xs" fontFamily="mono" color="fg.muted" flexShrink={0}>
              ID: {resource.id ?? "—"}
            </Text>
            {display.date && (
              <Text fontSize="sm" fontWeight="medium" color="fg">
                {display.date}
              </Text>
            )}
          </HStack>
          {display.status && (
            <Badge colorPalette="blue" fontSize="xs" px={2} py={1}>
              {display.status}
            </Badge>
          )}
        </Flex>

        <Heading size="sm" color="fg">
          {display.name}
        </Heading>

        {display.code && (
          <HStack gap={2}>
            <Text fontSize="xs" color="fg.muted" fontWeight="medium">
              Code:
            </Text>
            <Text fontSize="xs" color="fg.muted" fontFamily="mono">
              {display.code}
            </Text>
          </HStack>
        )}

        {display.value && (
          <Box bg="blue.50" borderLeft="4px" borderColor="blue.500" p={3} borderRadius="md">
            <HStack justify="space-between" align="center">
              <VStack align="start" gap={0}>
                <Text fontSize="xs" color="fg.muted" fontWeight="medium">
                  Value
                </Text>
                <Text fontSize="xl" fontWeight="bold" color="fg">
                  {display.value}
                </Text>
              </VStack>
              {display.interpretation && (
                <Badge
                  colorPalette={display.interpretationColor ?? "blue"}
                  fontSize="sm"
                  px={3}
                  py={1}
                  borderRadius="full"
                >
                  {display.interpretation}
                </Badge>
              )}
            </HStack>
          </Box>
        )}

        {display.components && display.components.length > 0 && (
          <Box>
            <Text fontSize="sm" fontWeight="semibold" color="fg" mb={2}>
              Components
            </Text>
            <VStack gap={2} align="stretch">
              {display.components.map((component, index) => (
                <HStack key={index} bg="bg.subtle" p={2} borderRadius="md" justify="space-between">
                  <Text fontSize="sm" color="fg.muted">
                    {component.name}
                  </Text>
                  <Text fontSize="sm" fontWeight="semibold" color="fg">
                    {component.value}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </Box>
        )}
      </VStack>
    </Box>
  );
}
